import { useState, type FormEvent, type ReactNode } from "react";

import Button from "components/Button";
import NoticeBanner from "components/NoticeBanner";
import { WizardTextField } from "components/WizardFields";
import {
  IDENTIFIER_LABEL,
  identifierError,
  identifierMethodOf,
} from "pages/StartLead/signIn";

import type { GrantMethod } from "./login-grant-flow";

/**
 * The one field a code or link is asked for with: an email or a US mobile
 * phone. It checks what was typed, tells the host which method the value
 * would be sent by (null until it is valid) so the button and the note under
 * it can say so, and hands a valid value to `onSubmit`. What happens next,
 * and whether the request is under way (`busy`), is the host's.
 */
export default function IdentifierForm({
  id,
  heading,
  notice,
  initialValue = "",
  busy,
  onSubmit,
  buttonLabel,
  loadingLabel,
  renderNote,
  children,
}: {
  id: string;
  heading?: ReactNode;
  /** An error about the last attempt, shown above the field. */
  notice: string | null;
  /** What to start the field with, such as what was typed before. */
  initialValue?: string;
  busy: boolean;
  onSubmit: (identifier: string) => void;
  buttonLabel: (method: GrantMethod | null) => string;
  loadingLabel?: (method: GrantMethod | null) => string;
  /** Shown under the button, e.g. the consent disclosure. */
  renderNote?: (method: GrantMethod | null) => ReactNode;
  /** Shown last, under the note. */
  children?: ReactNode;
}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const method = identifierMethodOf(value);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const invalid = identifierError(value);
    setError(invalid);
    if (invalid === null) onSubmit(value);
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className="flex w-full flex-col gap-4 text-left">
        {heading}
        {notice && <NoticeBanner tone="error">{notice}</NoticeBanner>}
        <WizardTextField
          id={id}
          label={IDENTIFIER_LABEL}
          value={value}
          onChange={setValue}
          error={error}
          autoComplete="username"
          autoFocus
        />
        <Button
          type="submit"
          variant="continue"
          fullWidth
          isLoading={busy}
          loadingText={loadingLabel?.(method)}
        >
          {buttonLabel(method)}
        </Button>
        {renderNote?.(method)}
        {children}
      </div>
    </form>
  );
}
